import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
// eslint-disable-next-line no-restricted-imports
import * as Sentry from "@sentry/react";
import { exists as i18nExists, t } from "i18next";
import { toast } from "react-toastify";
import { setUserToken } from "../store/slices/auth";
import { dispatch, store } from "../store/store";
import { ErrorDescription } from "../types/handled_error";
import history from "../utils/history";
import { stringUtils } from "../utils/stringUtils";
import { isHandled } from "./handledErrors";

export type ApiError = {
  error: string;
  status: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sub_errors: any;
};

class Api {
  baseURL: string;
  client: AxiosInstance;

  /**
   * constructor
   * Create an instance of the memoirsApi class.
   *
   * @param {string} baseURL
   */
  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.client = axios.create({
      baseURL,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    this.client.interceptors.response.use(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (response: any) => {
        if (response.headers.authorization !== undefined) {
          dispatch(setUserToken(response.headers.authorization));
        }
        return response;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error: any) => {
        if (error.code === "ERR_NETWORK") {
          toast.error(t("errors.network"));
        } else {
          const errorDescription: ErrorDescription = {
            endpoint: this.getEndpoint(error?.config?.url),
            method: error?.config?.method,
            responseStatus: error?.response?.status,
            error: error?.response?.data?.error_code,
          };
          Sentry.captureException(error, {
            tags: {
              endpoint: String(errorDescription.endpoint),
              method: errorDescription.method,
              responseStatus: errorDescription.responseStatus,
              errorCode: errorDescription.error,
            },
            extra: {
              responseData: error?.response?.data,
              requestConfig: error?.config,
            },
          });
          if (!isHandled(errorDescription)) {
            const errorCode =
              error?.response?.data?.error_code || error?.response?.data;
            const errorDetails: Record<string, string> | undefined =
              error?.response?.data?.error_details ||
              error?.response?.data?.error ||
              error?.response?.data;
            if (errorCode && i18nExists(`errors.${errorCode}`)) {
              toast.error(t(`errors.${errorCode}`, errorDetails));
            } else {
              if (
                errorDescription.responseStatus === 401 &&
                typeof errorDetails === "string" &&
                errorDetails === "Signature has expired"
              ) {
                dispatch(setUserToken(null));
                toast.error(t("errors.sessionExpired"));
                history.push("/login");
              } else if (
                typeof errorDetails === "string" &&
                errorDetails ===
                  "You have to confirm your email address before continuing." &&
                // An admin impersonating must never be trapped on this page
                !store.getState().auth.impersonating
              ) {
                history.push("/confirm-email");
                document.location.reload();
              } else if (
                error?.response?.data?.errors &&
                (Object.keys(error?.response?.data?.errors).includes(
                  "password"
                ) ||
                  Object.keys(error?.response?.data?.errors).includes(
                    "password_confirmation"
                  ))
              ) {
                toast.error(t("errors.passwordMustMatch"));
              } else {
                toast.error(
                  `${t("errors.error")}: ${
                    errorDescription.responseStatus
                  } \n${t("errors.details")}: ${t(
                    `errors.${errorDetails}`
                  )} \n${t("errors.contactIfPersist")}`,
                  {
                    className: "whitespace-pre-line",
                  }
                );
              }
            }
          }
        }
        return Promise.reject(error);
      }
    );
  }

  get = async <T>(path: string, config?: AxiosRequestConfig): Promise<T> => {
    const result = await this.client.get<T>(path, config);
    return result.data;
  };

  post = async <T>(
    path: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const result = await this.client.post<T>(path, data, config);
    return result.data;
  };

  put = async <T>(
    path: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const result = await this.client.put<T>(path, data, config);
    return result.data;
  };

  patch = async <T>(
    path: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    const result = await this.client.patch<T>(path, data, config);
    return result.data;
  };

  delete = async <T>(path: string, config?: AxiosRequestConfig): Promise<T> => {
    const result = await this.client.delete<T>(path, config);
    return result.data;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFormData: (data: Record<string, any>, prefix: null | string) => FormData =
    (data, prefix = null) => {
      const formData = new FormData();
      for (const key in data) {
        if (data[key] != null) {
          if (Array.isArray(data[key])) {
            const formKey =
              prefix == null ? `${key}[]` : `${prefix}[${key}][]`;
            for (const i in data[key]) {
              formData.append(formKey, data[key][i]);
            }
          } else {
            const formKey = prefix == null ? key : `${prefix}[${key}]`;
            const formValue =
              typeof data[key] === "object" && "uri" in data[key]
                ? this.formFileData(data[key])
                : data[key];
            formData.append(formKey, formValue);
          }
        }
      }
      return formData;
    };

  getEndpoint = (endpoint: string): string => {
    const parsed_endpoint = endpoint;
    const path = parsed_endpoint.split("/");
    if (stringUtils.isStringNumerical(path.pop())) return path.join("/");
    return parsed_endpoint;
  };

  formFileData = (file: {
    name: string;
    uri: string;
    type: string;
  }): { name: string; uri: string; type: string } => {
    const match = /\.(\w+)$/.exec(file.uri);
    const type = match ? `image/${match[1]}` : `image`;
    return {
      name: file.name,
      uri: file.uri,
      type: file.type === "image" ? type : file.type,
    };
  };
}

export const memoirsApiInstance = new Api(
  import.meta.env.VITE_MEMOIRS_API_BASE_URL || ""
);
